import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Mail, MailCheck, ShieldCheck } from "lucide-react";
import { api, ApiError } from "../api";
import { useAuth } from "../auth";
import { Button, Field } from "../ui";

/**
 * Request form for a one-time sign-in link (/login-link). Same
 * enumeration-safe posture as the forgot-password page.
 */
export function LoginLinkRequestPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post("/auth/login-link", { email });
      setSent(true);
    } catch {
      setError("Something went wrong. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center -mt-6 -mx-4 sm:-mx-6 lg:-mx-8 py-10 px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-ink-800">Email me a sign-in link</h1>
          <p className="mt-1.5 text-ink-500 font-light">
            Skip the password — we'll send a one-time link that signs you in.
          </p>
        </div>
        <div className="card">
          {sent ? (
            <div className="text-center py-4">
              <MailCheck size={28} className="mx-auto text-brand-700 mb-3" />
              <p className="text-sm text-ink-700 mb-1.5 font-medium">
                Check your inbox
              </p>
              <p className="text-sm text-ink-500">
                If <strong>{email}</strong> has an account, a sign-in link is
                on its way. It works once and expires in 15 minutes.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-1">
              <Field label="Email">
                <div className="relative">
                  <Mail
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
                  />
                  <input
                    className="input pl-9"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    placeholder="you@example.com"
                  />
                </div>
              </Field>
              {error && <div className="alert-error">{error}</div>}
              <Button
                variant="primary"
                type="submit"
                className="w-full mt-2"
                loading={busy}
                disabled={busy || !email.trim()}
              >
                {busy ? "Sending…" : "Send my sign-in link"}
                {!busy && <ArrowRight size={16} />}
              </Button>
            </form>
          )}
        </div>
        <p className="mt-4 text-center text-sm text-ink-500">
          Prefer your password?{" "}
          <Link
            to="/login"
            className="font-medium text-brand-700 hover:text-brand-800"
          >
            Back to sign in
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

/**
 * Landing page for the emailed link (/login/link?token=…). Exchanges
 * the single-use token for a session on load. Accounts with MFA
 * enrolled still get the TOTP second step — the link only replaces
 * the password.
 */
export function LoginLinkExchangePage() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [status, setStatus] = useState<"working" | "mfa" | "failed">(
    "working",
  );
  const [error, setError] = useState<string | null>(null);
  const [mfaChallenge, setMfaChallenge] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaUseBackup, setMfaUseBackup] = useState(false);
  const [busy, setBusy] = useState(false);
  // React 18 StrictMode double-fires effects in dev; the token is
  // single-use, so the second exchange would burn on "already used".
  const exchangedRef = useRef(false);

  useEffect(() => {
    if (exchangedRef.current) return;
    exchangedRef.current = true;
    if (!token) {
      setStatus("failed");
      setError("This page needs the sign-in link from your email.");
      return;
    }
    void (async () => {
      try {
        const r = await api.post<
          | { ok: true }
          | { mfa_required: true; challengeToken: string; expiresAt: string }
        >("/auth/login-link/exchange", { token });
        if ("mfa_required" in r && r.mfa_required) {
          setMfaChallenge(r.challengeToken);
          setStatus("mfa");
          return;
        }
        await refresh();
        nav("/projects", { replace: true });
      } catch (err) {
        setStatus("failed");
        const code =
          err instanceof ApiError
            ? (err.body as { error?: string })?.error
            : undefined;
        setError(
          code === "token_expired"
            ? "This sign-in link has expired."
            : "This sign-in link isn't valid any more — it may have been used already.",
        );
      }
    })();
  }, [token, refresh, nav]);

  const onSubmitMfa = async (e: FormEvent) => {
    e.preventDefault();
    if (!mfaChallenge) return;
    setError(null);
    setBusy(true);
    try {
      await api.post("/auth/login/mfa", {
        challengeToken: mfaChallenge,
        ...(mfaUseBackup
          ? { backupCode: mfaCode.trim() }
          : { code: mfaCode.trim() }),
      });
      await refresh();
      nav("/projects", { replace: true });
    } catch (err) {
      const code =
        err instanceof ApiError
          ? (err.body as { error?: string })?.error
          : undefined;
      if (code === "challenge_invalid") {
        setStatus("failed");
        setError("Your sign-in expired. Request a fresh link.");
      } else {
        setError(
          mfaUseBackup
            ? "That backup code didn't match or has already been used."
            : "That authenticator code didn't match. Try the next one.",
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center -mt-6 -mx-4 sm:-mx-6 lg:-mx-8 py-10 px-4">
      <div className="w-full max-w-md">
        <div className="card">
          {status === "working" && (
            <div className="text-center py-6 text-sm text-ink-600">
              <span className="spinner text-brand-700 mr-2" />
              Signing you in…
            </div>
          )}
          {status === "mfa" && (
            <form onSubmit={onSubmitMfa} className="space-y-1">
              <div className="mb-3 inline-flex items-center gap-2 rounded-md bg-status-approved-bg px-2.5 py-1 text-xs text-status-approved-fg">
                <ShieldCheck size={14} />
                Two-factor required
              </div>
              <p className="text-sm text-ink-700 mb-3">
                Your link checked out. Enter the{" "}
                {mfaUseBackup ? "backup" : "6-digit"} code from your
                authenticator app to finish signing in.
              </p>
              <Field label={mfaUseBackup ? "Backup code" : "Authenticator code"}>
                <input
                  className="input"
                  inputMode={mfaUseBackup ? "text" : "numeric"}
                  autoComplete="one-time-code"
                  maxLength={mfaUseBackup ? 12 : 6}
                  value={mfaCode}
                  onChange={(e) => {
                    const v = mfaUseBackup
                      ? e.target.value.toUpperCase()
                      : e.target.value.replace(/\D/g, "");
                    setMfaCode(v);
                  }}
                  required
                  autoFocus
                />
              </Field>
              {error && <div className="alert-error">{error}</div>}
              <Button
                variant="primary"
                type="submit"
                className="w-full mt-2"
                loading={busy}
                disabled={busy || mfaCode.length < 6}
              >
                {busy ? "Verifying…" : "Verify and sign in"}
                {!busy && <ArrowRight size={16} />}
              </Button>
              <p className="mt-3 text-xs">
                <button
                  type="button"
                  className="text-brand-700 hover:text-brand-800 bg-transparent border-0 p-0 cursor-pointer"
                  onClick={() => {
                    setMfaUseBackup((b) => !b);
                    setMfaCode("");
                    setError(null);
                  }}
                >
                  {mfaUseBackup
                    ? "Use authenticator code instead"
                    : "Use a backup code instead"}
                </button>
              </p>
            </form>
          )}
          {status === "failed" && (
            <div className="text-center py-4">
              <p className="text-sm text-ink-700 mb-2">{error}</p>
              <p className="text-sm text-ink-500">
                <Link
                  to="/login-link"
                  className="font-medium text-brand-700 hover:text-brand-800"
                >
                  Request a fresh sign-in link
                </Link>{" "}
                or{" "}
                <Link
                  to="/login"
                  className="font-medium text-brand-700 hover:text-brand-800"
                >
                  sign in with your password
                </Link>
                .
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
